import React, { useState } from 'react';
import {
  GripVertical,
  ArrowUp,
  ArrowDown,
  Copy,
  Eye,
  EyeOff,
  Trash2,
} from 'lucide-react';
import { useEditMode } from '../context/EditModeContext';

/**
 * EditableSection
 * --------------------------------------------------------------------
 * Wraps a content block. In edit mode it shows a floating toolbar with
 * move-up / move-down / duplicate / show-hide / delete actions and adds
 * a dashed indigo outline on hover.
 *
 * Props:
 *   blockUid           string  - unique id for this block
 *   blockType          string  - shown as a small label in the toolbar
 *   children           JSX
 *   onMoveUp           ?() => void
 *   onMoveDown         ?() => void
 *   onDuplicate        ?() => void
 *   onDelete           ?() => void
 *   onToggleVisibility ?() => void
 *   isVisible          ?boolean (default true)
 *   dragHandleProps    ?{ attributes, listeners } - if this section is itself
 *                       inside an EditableList, pass these here so the drag
 *                       handle in the toolbar works.
 */
function ToolbarButton({ onClick, title, children, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      className="inline-flex items-center justify-center w-6 h-6 rounded text-gray-600 hover:text-indigo-700 hover:bg-indigo-50 disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

function EditableSection({
  blockUid,
  blockType = 'block',
  children,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onDelete,
  onToggleVisibility,
  isVisible = true,
  dragHandleProps,
}) {
  const { editMode } = useEditMode();
  const [hovered, setHovered] = useState(false);

  if (!editMode) {
    if (!isVisible) return null;
    return <>{children}</>;
  }

  return (
    <section
      data-block-uid={blockUid}
      data-block-type={blockType}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`relative ${hovered ? 'outline outline-1 outline-dashed outline-indigo-400' : ''} ${!isVisible ? 'opacity-50' : ''}`}
    >
      {hovered && (
        <div className="absolute top-2 right-2 z-40 flex items-center gap-0.5 px-1.5 py-1 bg-white border border-gray-200 rounded-md shadow-md">
          <span className="text-[10px] uppercase tracking-wide text-gray-400 px-1 select-none">
            {blockType}
          </span>
          <span className="w-px h-4 bg-gray-200 mx-0.5" />
          {dragHandleProps && (
            <button
              type="button"
              {...dragHandleProps.attributes}
              {...dragHandleProps.listeners}
              title="Drag to reorder"
              aria-label="Drag to reorder"
              className="inline-flex items-center justify-center w-6 h-6 rounded text-gray-600 hover:text-indigo-700 hover:bg-indigo-50 cursor-grab active:cursor-grabbing"
            >
              <GripVertical size={14} />
            </button>
          )}
          <ToolbarButton onClick={onMoveUp} title="Move up" disabled={!onMoveUp}>
            <ArrowUp size={14} />
          </ToolbarButton>
          <ToolbarButton onClick={onMoveDown} title="Move down" disabled={!onMoveDown}>
            <ArrowDown size={14} />
          </ToolbarButton>
          <ToolbarButton onClick={onDuplicate} title="Duplicate" disabled={!onDuplicate}>
            <Copy size={14} />
          </ToolbarButton>
          <ToolbarButton
            onClick={onToggleVisibility}
            title={isVisible ? 'Hide' : 'Show'}
            disabled={!onToggleVisibility}
          >
            {isVisible ? <Eye size={14} /> : <EyeOff size={14} />}
          </ToolbarButton>
          <ToolbarButton onClick={onDelete} title="Delete" disabled={!onDelete}>
            <Trash2 size={14} className="text-red-500" />
          </ToolbarButton>
        </div>
      )}

      {children}
    </section>
  );
}

export default EditableSection;
